/**
 * Created by claudio iozzia on 22/06/2017.
 */
import {IAppSpec} from "../../Redux/selector.state";
import {
  evaluateUntilFirstValidator,
  evaluateUntilFirstValidatorOutput,
  IValidator, ValidatorFn, ValidatorGlobalOutputFn, ValidatorInputFn, ValidatorOutputFn, ValidatorSingleOutputFn,
  Validator,
  validatorFn_None, ValidatorSpecSelfFn
} from "./IValidator";
import {Ic, IEntityAlert, ITranslatedObj} from "../common";
import {EntityError} from "../EntityAlert";
import {Utils} from "../../common/utils";
import {isNullOrUndefined} from "util";

const validatorFn_NumOutput = (ic: Ic, spec: IAppSpec): IEntityAlert => {
  let result: ITranslatedObj = null;
  if (!isNullOrUndefined(ic)) {
    const maxNumOutput: number = Utils.getFieldValue(ic, 'designData.maxNumOutput', 1);
    const minNumOutput: number = Utils.getFieldValue(ic, 'designData.minNumOutput', 1);

    if (spec.spec.value.output.outputs.length < minNumOutput) {
      result = {
        key: 'supports min num outputs',
        data: { partNumber: ic.partNumber, minNumOutput}
      };
    }

    if (spec.spec.value.output.outputs.length > maxNumOutput) {
      result = {
        key: 'supports max num outputs',
        data: { partNumber: ic.partNumber, maxNumOutput}
      };
    }
  }

  return new EntityError(result);
};

const validatorFn_VinMin: ValidatorFn = (a: Ic, spec: IAppSpec): IEntityAlert => {
  const vMin = spec.spec.value.input.min;
  const vMax = spec.spec.value.input.max;
  let result: ITranslatedObj = null;

  if (a == null) {
    result = { key: Utils.isNumberRequired(vMin, "vin min")};
    if (result === null || result.key === null) {
      result = Utils.acceptUpTo(vMin, vMax, "Vin min", "Vin max");
    }
  } else {
    if (Utils.isValueEmpty(vMin) && Utils.isValueEmpty(vMax)) {
      result = Utils.acceptInRange(Number.MIN_VALUE, a.vin_min, a.vin_max, "Vin min", "V");
    } else {
      const min_max_ratio = Utils.getFieldValue(a, "validator.min_max_ratio_perc", NaN) / 100;
      if (!Utils.isValueEmpty(vMin)) {
        if (isNaN(min_max_ratio) || !Utils.isInRange(vMax, a.vin_min, a.vin_max)) {
          result = Utils.acceptInRange(vMin, a.vin_min, a.vin_max, "Vin min", "V");
        } else if (Utils.isInRange(vMax, a.vin_min, a.vin_max)) {
          const vin_lb: number = Math.max(a.vin_min, Math.ceil(vMax * min_max_ratio));
          result = Utils.acceptInRange(vMin, vin_lb, vMax, "Respect Vin max, Vin min", "V");
        }
      }
    }
  }
  return new EntityError(result);
};

const validatorFn_VinMax: ValidatorFn = (a: Ic, spec: IAppSpec): IEntityAlert => {
  const vMin = spec.spec.value.input.min;
  const vMax = spec.spec.value.input.max;
  let result: ITranslatedObj = null;

  if (a == null) {
    result = { key: Utils.isNumberRequired(vMax, "vin max")};
    if (result === null || result.key === null) {
      result = Utils.acceptGreaterThan(vMax, vMin, "Vin max", "Vin min");
    }
  } else {
    if (Utils.isValueEmpty(vMin) && Utils.isValueEmpty(vMax)) {
      result = Utils.acceptInRange(Number.MIN_VALUE, a.vin_min, a.vin_max, "Vin max", "V");
    } else {
      const min_max_ratio = Utils.getFieldValue(a, "validator.min_max_ratio_perc", NaN) / 100;
      if (!Utils.isValueEmpty(vMin)) {
        if (isNaN(min_max_ratio) || !Utils.isInRange(vMin, a.vin_min, a.vin_max)) {
          const vMax_LowerBound: number = Math.max(vMin, a.vin_min);
          result = Utils.acceptInRange(vMax, vMax_LowerBound, a.vin_max, "Vin max", "V");
        } else if (Utils.isInRange(vMin, a.vin_min, a.vin_max)) {
          const vin_ub: number = Math.min(Math.floor(vMin / min_max_ratio), a.vin_max);
          result = Utils.acceptInRange(vMax, vMin, vin_ub, "Respect Vin min, Vin max", "V");
        }
      }
    }
  }
  return new EntityError(result);
};

const validatorFn_SingleOutput_Voltage_Required: ValidatorOutputFn = (a: Ic, spec: IAppSpec, o: number): IEntityAlert => {
  let result: ITranslatedObj = null;
  const _vOut = spec.spec.value.output.outputs[o].voltage;

  if (Utils.isValueEmpty(_vOut)) {
    result = {
      key: 'vout is required'
    };
  }
  return new EntityError(result);
};

const validatorFn_SingleOutput_Current_Required: ValidatorOutputFn = (a: Ic, spec: IAppSpec, o: number): IEntityAlert => {
  let result: ITranslatedObj = null;
  const _iOut = spec.spec.value.output.outputs[o].current;

  if (Utils.isValueEmpty(_iOut)) {
    result = {
      key: 'iout is required'
    };
  }
  return new EntityError(result);
};

const validatorFn_SingleOutput_Current_Positive: ValidatorOutputFn = (a: Ic, spec: IAppSpec, o: number): IEntityAlert => {
  const _iOut = spec.spec.value.output.outputs[o].current;

  const result = {
    key: Utils.acceptNotNegative(_iOut, "iout")
  };
  return new EntityError(result);
};

// tslint:disable-next-line:class-name
export class Validator_Common extends Validator {
  public static createValidator(a: Ic): IValidator {
    return new Validator_Common();
  }

  protected get specValidation(): ValidatorSpecSelfFn {
    return validatorFn_NumOutput;
  }

  protected get inputMin(): ValidatorFn {
    return validatorFn_VinMin;
  }

  protected get inputMax(): ValidatorFn {
    return validatorFn_VinMax;
  }

  protected get inputFreq(): ValidatorFn {
    return validatorFn_None;
  }

  protected get input(): ValidatorInputFn {
    return (a: Ic, s: IAppSpec) => {
      return {
        min: this.inputMin(a, s),
        max: this.inputMax(a, s),
        freq: this.inputFreq(a, s)
      };
    };
  }

  protected singleOutputV(): ValidatorOutputFn[] {
    return [validatorFn_SingleOutput_Voltage_Required];
  }

  protected singleOutputI(): ValidatorOutputFn[] {
    return [validatorFn_SingleOutput_Current_Required, validatorFn_SingleOutput_Current_Positive];
  }

  protected singleOutputP(): ValidatorOutputFn[] {
    return [];
  }

  protected get singleOutput(): ValidatorSingleOutputFn {
    return (a: Ic, s: IAppSpec, o: number) => {
      return {
        voltage: evaluateUntilFirstValidatorOutput(this.singleOutputV())(a, s, o),
        current: evaluateUntilFirstValidatorOutput(this.singleOutputI())(a, s, o),
        power: evaluateUntilFirstValidatorOutput(this.singleOutputP())(a, s, o),
      };
    };
  }

  protected globalOutputV(): ValidatorFn[] {
    return [];
  }

  protected globalOutputI(): ValidatorFn[] {
    return [];
  }

  protected globalOutputP(): ValidatorFn[] {
    return [];
  }

  protected get globalOutput(): ValidatorGlobalOutputFn {
    return (ic: Ic, s: IAppSpec) => {
      return {
        voltage: evaluateUntilFirstValidator(this.globalOutputV())(ic, s),
        current: evaluateUntilFirstValidator(this.globalOutputI())(ic, s),
        power: evaluateUntilFirstValidator(this.globalOutputP())(ic, s),
      };
    };
  }

}

